// Walk an atomic element JSON and emit one row per supported sub-field,
// driven by atomicAnimationSchema. Element-level boxAnimation /
// boxExitAnimation live on the Redux box and are surfaced by TrackClip;
// only nested sub-fields belong here.
//
// Returns { animations: [{path, label, kind, entry, exit}], maxAnimationMs }.
// `maxAnimationMs` only counts actual animations so empty placeholder rows
// don't extend the scene timer.

import { getElementSchema } from "./atomicAnimationSchema";
import { humanizeFieldPath } from "../../../_editor/components/timeline/IdeaTimeline/utils/humanizeFieldPath";

function readAnim(obj, key) {
  if (!obj || typeof obj !== "object") return null;
  const spec = obj[key];
  if (!spec || typeof spec !== "object") return null;
  const a = spec.animation;
  if (!a) return null;
  const name = typeof a === "string" ? a : a?.name;
  if (!name) return null;
  const duration = typeof spec.duration === "number" ? spec.duration : 0.6;
  const delay = typeof spec.delay === "number" ? spec.delay : 0;
  return { name, duration, delay };
}

function readContainer(node) {
  if (typeof node === "string") return { entry: null, exit: null };
  if (!node || typeof node !== "object") return { entry: null, exit: null };
  return {
    entry: readAnim(node, "boxAnimation"),
    exit: readAnim(node, "boxExitAnimation"),
  };
}

function shortLabel(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return fallback;
    // Unresolved i18n keys from sample data shouldn't leak into the label.
    if (s.startsWith("__i18n:")) return fallback;
    return s.length > 28 ? s.slice(0, 28) + "…" : s;
  }
  if (typeof value === "object" && typeof value.text === "string") {
    return shortLabel(value.text, fallback);
  }
  return fallback;
}

// Mirror the renderer's "skip missing optional fields" guard so we don't
// emit rows for fields the user can't see on canvas.
function isFieldPresent(node) {
  if (node == null) return false;
  if (typeof node === "string") return true;
  if (typeof node === "object") return true;
  return Boolean(node);
}

function pushScalar(out, container, fieldKey, pathPrefix, kind, labelOverride) {
  const node = container ? container[fieldKey] : undefined;
  if (!isFieldPresent(node)) return;
  const path = pathPrefix ? `${pathPrefix}.${fieldKey}` : fieldKey;
  const { entry, exit } = readContainer(node);
  out.push({
    path,
    label: labelOverride || humanizeFieldPath(path),
    kind: kind || "scalar",
    entry,
    exit,
  });
}

function pushArrayItems(out, container, arrKey, itemCfg, pathPrefix) {
  const arr = container ? container[arrKey] : undefined;
  if (!Array.isArray(arr)) return;
  arr.forEach((item, i) => {
    const basePath = pathPrefix ? `${pathPrefix}.${arrKey}.${i}` : `${arrKey}.${i}`;
    const { entry, exit } = readContainer(item);
    const hintFromContent =
      itemCfg.labelFrom && item && typeof item === "object"
        ? shortLabel(item[itemCfg.labelFrom], null)
        : null;
    const fallbackLabel = humanizeFieldPath(basePath);
    const label = hintFromContent ? `${fallbackLabel} · ${hintFromContent}` : fallbackLabel;
    out.push({
      path: basePath,
      label,
      kind: itemCfg.kind === "container" ? "item-box" : itemCfg.kind || "item-box",
      entry,
      exit,
    });
    if (itemCfg.kind === "container" && item && typeof item === "object") {
      walkContainer(out, item, itemCfg, basePath);
    }
  });
}

function walkContainer(out, container, cfg, pathPrefix) {
  if (!cfg) return;
  const labels = cfg.labels || {};
  if (Array.isArray(cfg.scalars)) {
    for (const key of cfg.scalars) {
      pushScalar(out, container, key, pathPrefix, "scalar", labels[key]);
    }
  }
  if (Array.isArray(cfg.scalarBoxes)) {
    for (const key of cfg.scalarBoxes) {
      pushScalar(out, container, key, pathPrefix, "scalar-box", labels[key]);
    }
  }
  // When the modern `columns[]` array is in use, skip the legacy
  // left/right fallback to avoid duplicating rows.
  const consumedArrayKeys = new Set();
  if (cfg.arrays && typeof cfg.arrays === "object") {
    for (const arrKey of Object.keys(cfg.arrays)) {
      if (Array.isArray(container?.[arrKey])) consumedArrayKeys.add(arrKey);
      pushArrayItems(out, container, arrKey, cfg.arrays[arrKey], pathPrefix);
    }
  }
  if (cfg.columns && typeof cfg.columns === "object" && !consumedArrayKeys.has("columns")) {
    for (const colKey of Object.keys(cfg.columns)) {
      const colCfg = cfg.columns[colKey];
      const colNode = container ? container[colKey] : undefined;
      if (!isFieldPresent(colNode)) continue;
      const colPath = pathPrefix ? `${pathPrefix}.${colKey}` : colKey;
      if (colCfg.kind === "item-box" || colCfg.kind === "text-input") {
        const { entry, exit } = readContainer(colNode);
        const hintFromContent =
          colCfg.labelFrom && colNode && typeof colNode === "object"
            ? shortLabel(colNode[colCfg.labelFrom], null)
            : null;
        const fallbackLabel = humanizeFieldPath(colPath);
        const label = hintFromContent ? `${fallbackLabel} · ${hintFromContent}` : fallbackLabel;
        out.push({
          path: colPath,
          label,
          kind: colCfg.kind,
          entry,
          exit,
        });
      } else if (colCfg.kind === "container") {
        const { entry, exit } = readContainer(colNode);
        const hintFromContent =
          colCfg.labelFrom && colNode && typeof colNode === "object"
            ? shortLabel(colNode[colCfg.labelFrom], null)
            : null;
        const fallbackLabel = humanizeFieldPath(colPath);
        const label = hintFromContent ? `${fallbackLabel} · ${hintFromContent}` : fallbackLabel;
        out.push({
          path: colPath,
          label,
          kind: "item-box",
          entry,
          exit,
        });
        walkContainer(out, colNode, colCfg, colPath);
      } else {
        walkContainer(out, colNode || {}, colCfg, colPath);
      }
    }
  }
}

export function extractAnimations(element) {
  if (!element || typeof element !== "object") {
    return { animations: [], maxAnimationMs: 0 };
  }

  const schema = getElementSchema(element.type);
  const animations = [];

  if (schema && !schema.textOnly) {
    walkContainer(animations, element, schema, "");
  }

  let maxAnimationMs = 0;
  for (const { entry, exit } of animations) {
    for (const spec of [entry, exit]) {
      if (!spec) continue;
      const total = (spec.delay + spec.duration) * 1000;
      if (total > maxAnimationMs) maxAnimationMs = total;
    }
  }

  return { animations, maxAnimationMs };
}
